import dynamic from 'next/dynamic';
import { useEffect, useRef } from 'react';
import { Loading } from '~/elements';
import { useView } from '~/hooks';
import { useFormState } from '~/hooks';

import type { NextPage } from 'next';

const LookingGlassForm = dynamic<Dict>(
  () => import('~/components/looking-glass-form').then(i => i.LookingGlassForm),
  {
    loading: Loading,
  },
);

const Results = dynamic<Dict>(() => import('~/components/results').then(i => i.Results), {
  loading: Loading,
});

const Index: NextPage = () => {
  const view = useView();
  const resultsRef = useRef<HTMLDivElement>(null);
  const formRef = useRef<HTMLDivElement>(null);
  const prevView = useRef(view);

  useEffect(() => {
    if (prevView.current === 'form' && view === 'results') {
      // Transition form → results: scroll to terminal
      setTimeout(() => {
        resultsRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' });
      }, 80);
    } else if (prevView.current === 'results' && view === 'form') {
      // Transition results → form: scroll back to top
      formRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }
    prevView.current = view;
  }, [view]);

  return (
    <div
      className="w-full"
      style={{ scrollSnapType: 'y mandatory', overflowY: 'scroll', height: '100%' }}
      id="__snap-container"
    >
      {/* Section 1: Form – always full viewport height, can overflow on mobile */}
      <section
        ref={formRef}
        style={{ scrollSnapAlign: 'start', minHeight: '100%' }}
        className="flex flex-col items-center justify-center px-container-padding pt-16 pb-16"
      >
        <LookingGlassForm />
      </section>

      {/* Section 2: Results – only rendered when in results view */}
      {view === 'results' && (
        <section
          ref={resultsRef}
          style={{ scrollSnapAlign: 'start', minHeight: '100%' }}
          className="flex flex-col items-center justify-center px-container-padding py-4"
        >
          <Results />
        </section>
      )}
    </div>
  );
};

export default Index;
